import type { PlatformName } from '@/types'

export type Faq = {
    question: string
    answer: string
}

/* ------------------------------ Front page ------------------------------ */

export const frontBriefs = [
    { page: 'p.2', title: 'Culture index', body: 'Short videos up, stiff brand voices down. The full board, refreshed weekly.' },
    { page: 'p.4', title: 'The Forecast Board', body: 'Six services, read like a weather map. Hover for the small print.' },
    { page: 'p.11', title: 'Group Chat', body: 'Clients say nice things about us, mostly in lowercase.' }
]

/* ----------------------------- Culture ticker ---------------------------- */

export type CultureMove = {
    label: string
    change: number
}

export const cultureIndex: Array<CultureMove> = [
    { label: 'Short videos', change: 24 },
    { label: 'Stiff brand voices', change: -31 },
    { label: 'Behind the scenes', change: 18 },
    { label: 'Founders on camera', change: 27 },
    { label: 'Stock photo handshakes', change: -44 },
    { label: 'Captions people can search', change: 21 },
    { label: 'Trend audio, three days late', change: -26 },
    { label: 'Recurring series', change: 33 },
    { label: 'Logos in the first frame', change: -38 },
    { label: 'Replies under posts', change: 15 },
    { label: 'Hashtag soup', change: -19 },
    { label: 'Lo-fi with a plan', change: 12 }
]

/* -------------------------------- Manifesto ------------------------------- */

export const manifesto = 'Most brands post to be seen. We post to be remembered. Anyone can chase a trend. We read where the feed is heading, make things people send to a friend, and keep showing up until your name is the first one that comes to mind.'

export const manifestoHighlights = ['to be remembered.', 'send to a friend,', 'the first one that comes to mind.']

/* ----------------------------- Hunch to hit ------------------------------- */

export type Stage = {
    key: 'brief' | 'shoot' | 'post' | 'read'
    index: string
    title: string
    stamp: string
    body: string
}

export const stages: Array<Stage> = [
    {
        key: 'brief',
        index: '01',
        title: 'Brief',
        stamp: 'Mon 09:12',
        body: 'A hunch, a coffee and one page. We write down what the audience is craving and the single thing this month has to prove.'
    },
    {
        key: 'shoot',
        index: '02',
        title: 'Shoot',
        stamp: 'Wed 14:30',
        body: 'One afternoon, a tight shot list and a lot of takes. We film the month in blocks, so nothing gets made in a panic.'
    },
    {
        key: 'post',
        index: '03',
        title: 'Post',
        stamp: 'Thu 18:42',
        body: 'Every edit is cut for the app it lives on, then posted when your people are around and your rivals are quiet.'
    },
    {
        key: 'read',
        index: '04',
        title: 'Read the results',
        stamp: 'Mon 10:05',
        body: 'We read replies, saves and shares, not just likes. What worked gets a sequel. What did not gets an honest note.'
    }
]

/* ------------------------------ Content wall ------------------------------ */

export type WallTile =
    | { kind: 'photo', src: string, position?: string, views: string, platform: PlatformName, caption: string }
    | { kind: 'type', text: string, bg: string, fg: string, views: string, platform: PlatformName }

export const contentWall: Array<Array<WallTile>> = [
    [
        { kind: 'photo', src: '/images/work/fashion-1.jpg', position: '50% 20%', views: '1.4M', platform: 'instagram', caption: 'Darning Sundays, ep. 9' },
        { kind: 'type', text: 'Your jumper is older than your phone.', bg: '#ff5c00', fg: '#1e1e1e', views: '612K', platform: 'tiktok' },
        { kind: 'photo', src: '/images/work/food-1.jpg', position: '40% 55%', views: '2.3M', platform: 'tiktok', caption: 'The carrot reveal' },
        { kind: 'photo', src: '/images/work/lifestyle-2.jpg', position: '55% 60%', views: '88K', platform: 'pinterest', caption: 'Glaze test 14' },
        { kind: 'type', text: 'Three hooks we stole from a sandwich.', bg: '#1e1e1e', fg: '#f2efe9', views: '290K', platform: 'youtube' }
    ],
    [
        { kind: 'photo', src: '/images/work/tech-1.jpg', position: '50% 45%', views: '730K', platform: 'youtube', caption: 'The Desk Next Door' },
        { kind: 'photo', src: '/images/work/fitness-3.jpg', position: '45% 45%', views: '1.1M', platform: 'instagram', caption: 'Last One Home' },
        { kind: 'type', text: 'Send this to the friend who always burns.', bg: '#ffe27a', fg: '#1e1e1e', views: '402K', platform: 'instagram' },
        { kind: 'photo', src: '/images/work/beauty-2.jpg', position: '50% 50%', views: '518K', platform: 'tiktok', caption: 'The Forgetter, ep. 12' },
        { kind: 'photo', src: '/images/hero/run.jpg', position: '60% 35%', views: '96K', platform: 'instagram', caption: 'Thursday route, 6.2km' }
    ],
    [
        { kind: 'type', text: 'Count replies. Not likes.', bg: '#ebe6dd', fg: '#1e1e1e', views: '64K', platform: 'linkedin' },
        { kind: 'photo', src: '/images/work/fashion-3.jpg', position: '50% 25%', views: '880K', platform: 'tiktok', caption: 'Ten winters old' },
        { kind: 'photo', src: '/images/work/lifestyle-1.jpg', position: '50% 55%', views: '171K', platform: 'instagram', caption: 'Sunday broadcast: frost' },
        { kind: 'photo', src: '/images/work/fitness-1.jpg', position: '50% 50%', views: '143K', platform: 'instagram', caption: 'Kit review, unpaid' },
        { kind: 'type', text: 'Carol is ready. Are you?', bg: '#f1e2c8', fg: '#1e1e1e', views: '2.3M', platform: 'tiktok' }
    ]
]

/* --------------------------- Platform playground -------------------------- */

export type PlaygroundPlatform = {
    key: 'instagram' | 'tiktok' | 'youtube' | 'linkedin'
    label: string
    shape: 'circles' | 'zigzag' | 'frames' | 'grid'
    format: string
    note: string
    works: Array<string>
    skip: string
    stat: { value: string, label: string }
}

export const playground: Array<PlaygroundPlatform> = [
    {
        key: 'instagram',
        label: 'Instagram',
        shape: 'circles',
        format: 'Carousel, ten slides',
        note: 'The place people save things for later. Teach something, make it beautiful, and give them a reason to swipe to the end.',
        works: ['Carousels that teach one thing well', 'Reels with a strong first frame', 'Stories that ask a question'],
        skip: 'Posting the same reel you posted on TikTok, watermark and all.',
        stat: { value: '3.2x', label: 'more saves on carousels than single photos for our clients' }
    },
    {
        key: 'tiktok',
        label: 'TikTok',
        shape: 'zigzag',
        format: 'Vertical video, 21 seconds',
        note: 'A stage where the audience decides in a blink. Personality beats polish, and the comments are half the show.',
        works: ['A face in the first second', 'Series with a name and a day', 'Replying to comments with video'],
        skip: 'Anything that looks like it was approved by a legal team.',
        stat: { value: '68%', label: 'of our top TikToks started mid-sentence' }
    },
    {
        key: 'youtube',
        label: 'YouTube',
        shape: 'frames',
        format: 'Long video plus Shorts',
        note: 'The search engine people forget is a search engine. Answer real questions and your videos keep working for years.',
        works: ['Titles that match what people type', 'Thumbnails with one face and three words', 'Shorts that tease the long video'],
        skip: 'A five minute intro before anything happens.',
        stat: { value: '41%', label: 'of views on client channels come from search' }
    },
    {
        key: 'linkedin',
        label: 'LinkedIn',
        shape: 'grid',
        format: 'Text post with a document',
        note: 'A room full of people who pretend they are not on social media. Opinions, numbers and honest lessons travel furthest.',
        works: ['First person posts from real people', 'Documents that read like a short deck', 'A clear opinion in line one'],
        skip: 'Corporate announcements that start with we are thrilled.',
        stat: { value: '5x', label: 'reach for founder posts versus the company page' }
    }
]

/* ---------------------------- Post time machine --------------------------- */

export type PlannedPost = {
    day: string
    format: 'Reel' | 'Carousel' | 'Story' | 'Live' | 'Photo' | 'Short'
    caption: string
    time: string
}

export const industries = ['Fashion', 'Food', 'Tech', 'Beauty', 'Fitness', 'Lifestyle'] as const

export type Industry = typeof industries[number]

export const sampleWeeks: Record<Industry, Array<PlannedPost>> = {
    Fashion: [
        { day: 'Mon', format: 'Carousel', caption: 'Five ways to wear one coat, ranked by our intern', time: '12:30' },
        { day: 'Tue', format: 'Story', caption: 'Poll: cuffs rolled or down?', time: '08:15' },
        { day: 'Wed', format: 'Reel', caption: 'The coat, ten years later', time: '18:42' },
        { day: 'Thu', format: 'Photo', caption: 'Wearer of the week: Priya in Leeds', time: '13:00' },
        { day: 'Fri', format: 'Reel', caption: 'Packing for a weekend in one bag', time: '17:20' },
        { day: 'Sat', format: 'Live', caption: 'Studio sale, open the boxes with us', time: '11:00' },
        { day: 'Sun', format: 'Story', caption: 'Repair clinic questions, answered', time: '19:30' }
    ],
    Food: [
        { day: 'Mon', format: 'Reel', caption: 'What 6am looks like in our kitchen', time: '07:10' },
        { day: 'Tue', format: 'Carousel', caption: 'Three sauces, one jar, no fuss', time: '12:15' },
        { day: 'Wed', format: 'Story', caption: "This week's secret word", time: '16:00' },
        { day: 'Thu', format: 'Reel', caption: 'The cheese pull we were promised', time: '18:42' },
        { day: 'Fri', format: 'Photo', caption: 'Weekend specials, written in chalk', time: '11:30' },
        { day: 'Sat', format: 'Live', caption: 'Taste test with the regulars', time: '14:00' },
        { day: 'Sun', format: 'Story', caption: 'Leftover ideas from the menu', time: '18:00' }
    ],
    Tech: [
        { day: 'Mon', format: 'Short', caption: 'The setting nobody knows about', time: '09:00' },
        { day: 'Tue', format: 'Carousel', caption: 'What we changed after your feedback', time: '12:00' },
        { day: 'Wed', format: 'Reel', caption: 'Unboxed at the loudest desk in London', time: '17:45' },
        { day: 'Thu', format: 'Photo', caption: 'Founder note: why we said no', time: '08:30' },
        { day: 'Fri', format: 'Short', caption: 'Ask the engineer, round three', time: '16:30' },
        { day: 'Sat', format: 'Story', caption: 'Setup of the week from a customer', time: '10:30' },
        { day: 'Sun', format: 'Carousel', caption: 'Five shortcuts for a quieter Monday', time: '19:00' }
    ],
    Beauty: [
        { day: 'Mon', format: 'Story', caption: '8am reminder, you know the one', time: '08:00' },
        { day: 'Tue', format: 'Reel', caption: 'Texture check, no filter', time: '13:15' },
        { day: 'Wed', format: 'Carousel', caption: 'Your routine in three steps, not twelve', time: '18:30' },
        { day: 'Thu', format: 'Live', caption: 'Ask a dermatologist anything', time: '19:00' },
        { day: 'Fri', format: 'Reel', caption: 'Getting ready in under four minutes', time: '07:45' },
        { day: 'Sat', format: 'Photo', caption: 'Your shelfies, our favourite', time: '11:00' },
        { day: 'Sun', format: 'Story', caption: 'Swap shop: what to try next', time: '20:00' }
    ],
    Fitness: [
        { day: 'Mon', format: 'Reel', caption: 'Monday route, filmed at the back', time: '06:30' },
        { day: 'Tue', format: 'Carousel', caption: 'The warm-up nobody skips twice', time: '12:00' },
        { day: 'Wed', format: 'Story', caption: 'Pace groups for tonight', time: '16:30' },
        { day: 'Thu', format: 'Reel', caption: 'Last One Home, new episode', time: '21:00' },
        { day: 'Fri', format: 'Photo', caption: 'Member of the week', time: '09:00' },
        { day: 'Sat', format: 'Live', caption: 'Long run, live from mile eight', time: '08:00' },
        { day: 'Sun', format: 'Story', caption: 'Rest day playlist', time: '18:30' }
    ],
    Lifestyle: [
        { day: 'Mon', format: 'Photo', caption: 'The view from the studio window', time: '08:30' },
        { day: 'Tue', format: 'Reel', caption: 'The wheel at 6am, sound on', time: '07:00' },
        { day: 'Wed', format: 'Carousel', caption: 'How we pack a mug so it survives', time: '12:30' },
        { day: 'Thu', format: 'Story', caption: 'Glaze vote: moss or rust?', time: '17:00' },
        { day: 'Fri', format: 'Reel', caption: 'Kiln opening, fingers crossed', time: '18:42' },
        { day: 'Sat', format: 'Photo', caption: 'Your mugs in your kitchens', time: '10:00' },
        { day: 'Sun', format: 'Short', caption: 'Sunday broadcast: the forecast', time: '19:15' }
    ]
}

/* ------------------------------ Sticker sheet ----------------------------- */

export type Sticker = {
    word: string
    line: string
    shape: 'circle' | 'burst' | 'pill' | 'square' | 'flower' | 'arch'
    bg: string
    fg: string
    x: number
    y: number
    rotate: number
}

export const stickers: Array<Sticker> = [
    { word: 'Seen', line: 'In more feeds, on purpose', shape: 'circle', bg: '#ff5c00', fg: '#1e1e1e', x: 4, y: 6, rotate: -8 },
    { word: 'Consistent', line: 'Never goes quiet in a busy month', shape: 'pill', bg: '#1e1e1e', fg: '#f2efe9', x: 27, y: 10, rotate: 4 },
    { word: 'Talked About', line: 'Posts that end up in group chats', shape: 'burst', bg: '#ffe27a', fg: '#1e1e1e', x: 56, y: 3, rotate: 9 },
    { word: 'Measured', line: 'Replies, saves and sales, not vibes', shape: 'square', bg: '#f8f6f2', fg: '#1e1e1e', x: 79, y: 12, rotate: 6 },
    { word: 'Fast', line: 'Trend to finished post in 48 hours', shape: 'arch', bg: '#ff5c00', fg: '#1e1e1e', x: 6, y: 54, rotate: -5 },
    { word: 'Bold', line: 'Ideas with an actual opinion', shape: 'flower', bg: '#1e1e1e', fg: '#ffe27a', x: 28, y: 46, rotate: -10 },
    { word: 'Scalable', line: 'Formats that grow from one app to six', shape: 'pill', bg: '#ebe6dd', fg: '#1e1e1e', x: 52, y: 60, rotate: 7 },
    { word: 'Honest', line: 'We tell you when it did not work', shape: 'circle', bg: '#ffe27a', fg: '#1e1e1e', x: 80, y: 56, rotate: 3 }
]

/* ------------------------------- Scoreboard ------------------------------- */

export const scoreboard = [
    { value: 214, suffix: '', decimals: 0, caption: 'Campaigns launched. Two of them involved a jar of pickles.', points: [10, 16, 15, 24, 31, 30, 42, 50, 58, 66, 79, 92] },
    { value: 96, suffix: '%', decimals: 0, caption: 'Clients who stay past the first quarter. The other 4% moved to Bali.', points: [70, 74, 72, 80, 79, 84, 86, 85, 90, 92, 93, 96] },
    { value: 61, suffix: 'M', decimals: 0, caption: 'Views this year. Roughly one per person in the UK, if they all watched once.', points: [4, 7, 9, 14, 13, 22, 30, 36, 48, 61, 80, 100] },
    { value: 48, suffix: 'h', decimals: 0, caption: 'From rising trend to posted. Faster if the coffee machine behaves.', points: [100, 92, 88, 80, 74, 70, 63, 60, 57, 52, 50, 48] }
]

/* ---------------------------- Mistakes we fixed --------------------------- */

export type Mistake = {
    label: string
    bad: { title: string, detail: string }
    good: { title: string, detail: string }
    lesson: string
}

export const mistakes: Array<Mistake> = [
    {
        label: 'The essay graphic',
        bad: { title: 'We are thrilled to announce our new range of sustainable solutions for the modern home.', detail: '42 words on a beige square' },
        good: { title: 'Your new favourite mug survives the dishwasher. We checked 300 times.', detail: 'One line, one photo, one proof point' },
        lesson: 'If it needs a paragraph, it is a caption, not a graphic.'
    },
    {
        label: 'Logo first',
        bad: { title: 'Logo spins for two seconds. Then a slow pan across the office.', detail: 'Lost 71% of viewers by second three' },
        good: { title: 'Open on the result. A packed shop on a Tuesday.', detail: 'Kept 2.4x more viewers at second five' },
        lesson: 'Nobody followed you for your logo animation.'
    },
    {
        label: 'Stock handshake',
        bad: { title: 'Two suits shaking hands in front of a window.', detail: 'Zero saves, one confused comment' },
        good: { title: 'The actual team, mid-argument about a font.', detail: 'Most saved post of the quarter' },
        lesson: 'Real people beat pretend people every single time.'
    },
    {
        label: 'Hashtag soup',
        bad: { title: '#marketing #growth #business #success #motivation #mondaymood', detail: 'Thirty tags, no reach' },
        good: { title: 'How to fix a pulled thread in two minutes', detail: 'A caption people actually search for' },
        lesson: 'Write the words people type, not the words that feel busy.'
    },
    {
        label: 'Posting into the void',
        bad: { title: 'Posted at 3:17am because that is when it was ready.', detail: 'Buried by breakfast' },
        good: { title: 'Scheduled for 6:42pm on a quiet Thursday.', detail: 'Double the first hour replies' },
        lesson: 'Show up when your people are around and your rivals are not.'
    },
    {
        label: 'The ad that looks like an ad',
        bad: { title: 'Shop now. Limited time. Free shipping. Buy today.', detail: 'Scrolled past in half a second' },
        good: { title: 'A customer explaining why she bought three.', detail: 'Cost per sale dropped by a third' },
        lesson: 'People skip adverts. They watch stories.'
    }
]

/* --------------------------------- Wind map ------------------------------- */

export type WindCity = {
    city: string
    brand: string
    result: string
    lon: number
    lat: number
}

export const windCities: Array<WindCity> = [
    { city: 'London', brand: 'Quiet Labs', result: '3.4x return on ad spend', lon: -0.1, lat: 51.5 },
    { city: 'Bristol', brand: 'Pip & Ferment', result: '2.3M views on one jar', lon: -2.6, lat: 51.4 },
    { city: 'Leeds', brand: 'Mossline', result: '+268% reach in 12 weeks', lon: -1.5, lat: 53.8 },
    { city: 'Lisbon', brand: 'Casa Mar', result: 'Sold out a summer menu in 9 days', lon: -9.1, lat: 38.7 },
    { city: 'Berlin', brand: 'Kiez Coffee', result: '+52% weekday footfall', lon: 13.4, lat: 52.5 },
    { city: 'New York', brand: 'Sunday Skin', result: 'Three sell-outs in one summer', lon: -74, lat: 40.7 },
    { city: 'Toronto', brand: 'Northline Bikes', result: '18K waitlist before launch', lon: -79.4, lat: 43.7 },
    { city: 'Lagos', brand: 'Adire House', result: '1.6M views on a fabric reveal', lon: 3.4, lat: 6.5 },
    { city: 'Mumbai', brand: 'Chai Theory', result: '+61% online orders', lon: 72.9, lat: 19.1 },
    { city: 'Melbourne', brand: 'Lungs Run Club', result: 'Fourth city, 2,000 members', lon: 145, lat: -37.8 }
]

/* -------------------------------- Group chat ------------------------------ */

export type ChatMessage = {
    from: 'client' | 'us'
    text: string
    reaction?: 'heart' | 'fire' | 'laugh' | 'clap'
    time: string
}

export type ChatThread = {
    id: string
    name: string
    role: string
    initials: string
    tone: string
    preview: string
    messages: Array<ChatMessage>
}

export const chatThreads: Array<ChatThread> = [
    {
        id: 'mossline',
        name: 'Harriet, Mossline',
        role: 'Founder, Mossline',
        initials: 'HM',
        tone: '#b89b72',
        preview: 'my dad is now famous for darning',
        messages: [
            { from: 'client', text: 'ok so my dad is now recognised at the post office', time: '18:02' },
            { from: 'client', text: 'from the darning videos. he signed a jumper. A JUMPER.', time: '18:02', reaction: 'laugh' },
            { from: 'us', text: 'Episode 10 drops Sunday. Tell him to wear the good cardigan.', time: '18:04' },
            { from: 'client', text: 'also sales from instagram are up 44% this quarter which is frankly unhinged', time: '18:05', reaction: 'fire' },
            { from: 'client', text: 'best decision we made this year. not even close', time: '18:06', reaction: 'heart' }
        ]
    },
    {
        id: 'quiet-labs',
        name: 'Aiko, Quiet Labs',
        role: 'Head of Growth, Quiet Labs',
        initials: 'AB',
        tone: '#8fa3b3',
        preview: 'kevin has a fan club',
        messages: [
            { from: 'client', text: 'launch numbers are in. are you sitting down', time: '09:41' },
            { from: 'client', text: '3.4x return on spend. our board asked if it was a typo', time: '09:41', reaction: 'fire' },
            { from: 'us', text: 'Not a typo. Kevin did the heavy lifting, we just filmed him eating crisps.', time: '09:43', reaction: 'laugh' },
            { from: 'client', text: 'honestly the weekly calls are my favourite meeting. you tell us when things flop too, which is rare', time: '09:45' },
            { from: 'client', text: 'ok booking the next quarter now', time: '09:46', reaction: 'clap' }
        ]
    },
    {
        id: 'sunday-skin',
        name: 'Leila, Sunday Skin',
        role: 'Founder, Sunday Skin',
        initials: 'LH',
        tone: '#e3a98a',
        preview: 'sold out AGAIN',
        messages: [
            { from: 'client', text: 'we sold out again. third time this summer', time: '13:20', reaction: 'fire' },
            { from: 'client', text: 'people are tagging their friends in the 8am reminder like it is a public service', time: '13:21' },
            { from: 'us', text: 'It kind of is. Want The Forgetter to do a back to school special?', time: '13:24' },
            { from: 'client', text: 'yes. obviously. you lot just get it, every time', time: '13:25', reaction: 'heart' }
        ]
    }
]

/* ---------------------------- Hot take generator -------------------------- */

export const hotTakes: Record<Industry, Array<string>> = {
    Fashion: [
        'Film the same jacket on five real customers instead of one perfect model.',
        'Post your returns pile once a month and explain what went wrong.',
        'Let your oldest customer review your newest collection, on camera.',
        'Start a weekly repair show. Fixing things is the new unboxing.'
    ],
    Food: [
        'Name your sourdough starter and give it a weekly update.',
        'Film the dish nobody orders and ask the internet to save it.',
        'Post a secret menu word every Wednesday. Make people say it out loud.',
        'Show the 6am prep in real time, with no music and no talking.'
    ],
    Tech: [
        'Give your product a villain. Every great launch needs someone to annoy.',
        'Let your support team answer the meanest comment, live.',
        'Post the feature you cut and why you cut it.',
        'Hand a customer the phone for a full day of stories.'
    ],
    Beauty: [
        'Do a no-makeup review of your own product, filmed at 7am.',
        'Ask a dermatologist to roast your bestseller. Kindly.',
        'Make a send-this-to-your-friend reminder people actually forward.',
        'Show the shade that failed in testing and what you learned.'
    ],
    Fitness: [
        'Make the slowest person in class the main character.',
        'Post the workout you hate, with honest commentary.',
        'Film your coaches doing a class taught by a member.',
        'Turn your Thursday route into a story people can save.'
    ],
    Lifestyle: [
        'Film your studio like a weather report, every Sunday.',
        'Ask customers for photos of your product looking scruffy and loved.',
        'Do a live kiln opening with no idea how it will turn out.',
        'Post the seconds sale before you post the new collection.'
    ]
}

/* --------------------------- Behind the scenes ---------------------------- */

export const studioMoments = [
    { src: '/images/social/studio-1.jpg', position: '50% 50%', caption: 'whiteboard, 9:14am, mostly arrows', rotate: -4 },
    { src: '/images/social/studio-2.jpg', position: '50% 40%', caption: 'shoot day. third coffee.', rotate: 3 },
    { src: '/images/hero/latte.jpg', position: '50% 45%', caption: 'the coffee station (essential)', rotate: -2 },
    { src: '/images/social/studio-3.jpg', position: '50% 50%', caption: 'the calendar wall, all green', rotate: 5 },
    { src: '/images/social/creator-2.jpg', position: '50% 30%', caption: 'take 14 was the one', rotate: -5 },
    { src: '/images/services/fan-3.jpg', position: '50% 50%', caption: 'testing hooks on the bus', rotate: 2 },
    { src: '/images/social/studio-4.jpg', position: '50% 50%', caption: 'friday show and tell', rotate: -3 },
    { src: '/images/hero/podcast.jpg', position: '50% 50%', caption: 'the quiet room (not quiet)', rotate: 4 }
]

/* ---------------------------- Ask us anything ----------------------------- */

export const homeFaqs: Array<Faq> = [
    {
        question: 'How fast can you start?',
        answer: 'Usually within two weeks. The first week is a forecast: we read your accounts, your category and your audience, then come back with a plan. The second week we shoot.'
    },
    {
        question: 'Do you only do TikTok?',
        answer: 'Nope. Instagram, TikTok, YouTube, LinkedIn and Pinterest. We will tell you honestly which two or three are worth your time, and which ones to ignore for now.'
    },
    {
        question: 'Will it still sound like us?',
        answer: 'Better than that, it will sound like the best version of you. We start with a voice session, write a short no list of words you will never use, and one person on your side signs off.'
    },
    {
        question: 'What does a forecast actually include?',
        answer: 'A one page read of where your category is heading, three content ideas built for it, the platforms to focus on and the times your audience is around. It is free and it is yours to keep.'
    },
    {
        question: 'Do we have to sign a long contract?',
        answer: 'No. Plans run month to month after the first quarter. If it is not working, we would rather you leave than stay annoyed.'
    },
    {
        question: 'Can we be in the videos?',
        answer: 'We would love that. Founders and staff on camera are one of the strongest signals we see. We coach, we keep takes short and we never make anyone dance.'
    }
]

/* ------------------------------- Showreel --------------------------------- */

export type ShowreelScene = {
    image?: string
    position?: string
    eyebrow: string
    title: string
    detail?: string
}

export const showreel: Array<ShowreelScene> = [
    { eyebrow: 'Reel 2026', title: 'Make the internet talk about you.' },
    { image: '/images/work/fashion-1.jpg', position: '50% 25%', eyebrow: 'Mossline', title: '+268% reach', detail: 'in twelve weeks' },
    { image: '/images/work/food-2.jpg', position: '55% 50%', eyebrow: 'Pip & Ferment', title: '2.3M views', detail: 'on one jar' },
    { image: '/images/work/tech-1.jpg', position: '50% 50%', eyebrow: 'Quiet Labs', title: '3.4x ROAS', detail: 'in sixteen weeks' },
    { image: '/images/work/beauty-2.jpg', position: '50% 50%', eyebrow: 'Sunday Skin', title: '3 sell-outs', detail: 'in one summer' },
    { image: '/images/work/fitness-3.jpg', position: '45% 40%', eyebrow: 'Lungs Run Club', title: '7x community', detail: 'in a year' },
    { image: '/images/work/lifestyle-1.jpg', position: '50% 55%', eyebrow: 'Hearth & Hollow', title: '+71% sales', detail: 'from social' },
    { eyebrow: 'Your brand next', title: 'Catch the lift.' }
]
